import React, { useState, useImperativeHandle, useRef, forwardRef } from 'react'
import { TextField, Box, useTheme, Typography, Stack } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'
import Switch from 'packages/ui/Switch'
import Title from 'components/global/Title'

import useMessage from './use-message'

const Message = React.forwardRef(
  ({ defaultValue, disabled, isModal = false, onChange }, ref) => {
    const [message, setMessage] = useState(defaultValue || '')
    const theme = useTheme()

    useImperativeHandle(ref, () => ({
      reset(value) {
        setMessage(value)
      },
      getMessage() {
        return message
      }
    }))

    const handleChange = (e) => {
      const newMessage = e.target.value
      if (newMessage.length <= 360) {
        setMessage(newMessage)
        onChange(newMessage)
      }
    }

    return (
      <TextField
        fullWidth
        placeholder="Aucun message"
        value={message}
        onChange={handleChange}
        sx={{
          '&.MuiFormControl-root': {
            backgroundColor: 'white'
          },
          '& .MuiOutlinedInput-root': {
            border: `1px solid ${isModal ? '#C7D2E3' : theme.palette.grey[100]}`,
            borderRadius: '12px',
            backgroundColor: 'white',
            color: '#2D3748',
            '& fieldset': {
              border: 'none'
            },
            '&:hover': {
              borderColor: isModal ? '#9FB3CC' : theme.palette.grey[100]
            },
            '&.Mui-focused': {
              borderColor: '#2570EB',
              boxShadow: isModal ? '0 0 0 2px rgba(37, 112, 235, 0.12)' : 'none'
            }
          },
          '& .MuiInputBase-input': {
            color: '#2D3748',
            '&::placeholder': {
              color: '#6B7280',
              opacity: 1
            }
          }
        }}
        type="text"
        disabled={disabled}
        multiline
        rows={5}
        size="small"
      />
    )
  }
)



const MessageCard = forwardRef(({ rooms, messages, room, isModal = false, onMessageChange }, ref) => {
  const { t } = useTranslation()
  const messageRef = useRef(null)
  const { id } = useParams()
  const apiMessage = useMessage({ rooms, messages, room })
  const theme = useTheme()
  const [message, setMessage] = useState(apiMessage.message?.content || '')
  const [isEnabled, setIsEnabled] = useState(apiMessage.message?.enable || false)
  const [isLoadingSave, setIsLoadingSave] = useState(false)

  const handleMessageChange = (newMessage) => {
    setMessage(newMessage)
    onMessageChange?.(newMessage)
  }

  const handleCancel = () => {
    setMessage('')
    messageRef.current.reset(apiMessage.message.content)
    setIsEnabled(apiMessage.message.enable)
  }

  const handleSave = async () => {
    const currentMessage = message || messageRef.current?.getMessage() || ''
    if (!currentMessage.trim()) return

    setIsLoadingSave(true)
    try {
      const result = await apiMessage.saveContent(currentMessage)
      const messageId = result.data.message.id


      if (isEnabled !== apiMessage.message.enable) {
        isEnabled ? await apiMessage.enable(messageId) : await apiMessage.disable(messageId)
      } else {
        toast.success(t('stream-cast.room_message.toast.update_success'))
      }
    } catch (error) {
      console.error('Error saving message:', error)
    } finally {
      setIsLoadingSave(false)
    }
  }

  useImperativeHandle(ref, () => ({
    handleCancel,
    handleSave,
    getMessage: () => message || messageRef.current?.getMessage() || '',
    isLoadingSave: () => isLoadingSave,
    hasMessage: () => message.trim().length > 0
  }))

  const toggleMessageEnabled = () => {
    setIsEnabled(!isEnabled)
  }

  if ((!id || !rooms?.length || !apiMessage.room) && !isModal) {
    return null
  }

  const isMessageEmpty = !message.trim()

  return (
    <Box sx={{
      position: 'relative',
      gap: theme.spacing(1.5),
      backgroundColor: theme.palette.common.white,
      ...(isModal && {
        borderRadius: '24px',
        padding: theme.spacing(1.5, 0, 0)
      })
    }}>
      {isModal ? (
        <Box sx={{ px: 3, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 16, color: 'primary.main', fontWeight: '500' }}>
            {apiMessage.room?.site_name || ''}
          </Typography>
        </Box>
      ) : (
        <Title titleKey={t('stream-cast.room_message.title')} />
      )}

      <Box sx={{ px: isModal ? 3 : 0 }}>
        {!apiMessage.loading && (
          <Message
            key={apiMessage.room?.id || 'modal-message'}
            ref={messageRef}
            onChange={handleMessageChange}
            defaultValue={apiMessage.message.content}
            isModal={isModal}
          />
        )}
        {apiMessage.error && <p>{t('stream-cast.room_message.error')}</p>}

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2
        }}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Switch
                onClick={toggleMessageEnabled}
                checked={isEnabled}
                disabled={isMessageEmpty}
              />
              <Typography
                component="span"
                sx={{
                  color: 'text.body.secondary',
                  fontSize: 14
                }}
              >
                {t('stream-cast.card.message.active')}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{
            color: 'text.body.tertiary',
            fontSize: 12
          }}>
            {message.length} / 360
          </Box>
        </Box>
      </Box>
    </Box>
  )
})

export default MessageCard
